import b4a from 'b4a'
import z32 from 'z32'
import { domainHash, randomBytes, sign, verify, encrypt, decrypt } from '@pine/crypto'
import { PROTOCOL_VERSION, OP, LIMITS } from './constants.js'
import { opEncoding, PAYLOAD, encodeWith, decodeWith } from './encodings.js'

/**
 * Ops are signed by the author's *account* key, not by the hypercore that
 * carries them. That keeps authorship verifiable after a message is forwarded,
 * replicated through a relay, or re-linearised by Autobase, and it means a
 * compromised device core cannot forge messages from another member.
 */
export function opSignable (op) {
  return domainHash(
    'pine/op/v1',
    String(op.version ?? PROTOCOL_VERSION),
    String(op.type),
    op.id,
    op.author,
    String(op.timestamp),
    op.payload
  )
}

export function createOp (type, payload, identity, { timestamp = Date.now(), encryptionKey = null } = {}) {
  const codec = PAYLOAD[type]
  if (!codec) throw new Error('Unknown op type: ' + type)

  let body = encodeWith(codec, payload)
  if (encryptionKey) body = encrypt(body, encryptionKey)

  const op = {
    version: PROTOCOL_VERSION,
    type,
    id: randomBytes(32),
    author: identity.publicKey,
    timestamp,
    payload: body,
    signature: null
  }
  op.signature = sign(opSignable(op), identity.secretKey)
  return op
}

export function encodeOp (op) {
  return encodeWith(opEncoding, op)
}

/**
 * Decode and authenticate. Returns null for anything malformed, unsigned or
 * signed by someone other than the claimed author — the caller can then drop
 * the block without any further checks.
 */
export function decodeOp (block, { encryptionKey = null } = {}) {
  let op
  try {
    op = decodeWith(opEncoding, block)
  } catch {
    return null
  }
  if (op.version !== PROTOCOL_VERSION) return null
  if (!verify(opSignable(op), op.signature, op.author)) return null

  const codec = PAYLOAD[op.type]
  if (!codec) return null

  let body = op.payload
  if (encryptionKey) {
    body = decrypt(body, encryptionKey)
    if (!body) return null
  }

  let value
  try {
    value = decodeWith(codec, body)
  } catch {
    return null
  }
  if (!validate(op.type, value)) return null

  return { ...op, value }
}

/** Structural sanity limits, applied before an op can touch the view. */
export function validate (type, v) {
  switch (type) {
    case OP.MESSAGE:
      if (typeof v.text !== 'string' || v.text.length > LIMITS.MESSAGE_TEXT) return false
      if (v.attachments.length > LIMITS.ATTACHMENTS_PER_MESSAGE) return false
      if (v.entities.length > LIMITS.ENTITIES_PER_MESSAGE) return false
      for (const e of v.entities) if (e.offset < 0 || e.length < 0) return false
      return true
    case OP.EDIT:
      return typeof v.text === 'string' && v.text.length <= LIMITS.MESSAGE_TEXT
    case OP.REACTION:
      return typeof v.emoji === 'string' && v.emoji.length > 0 && v.emoji.length <= LIMITS.REACTION_LENGTH
    case OP.ROOM_META:
      if (v.name.length > LIMITS.ROOM_NAME) return false
      if (v.description.length > LIMITS.ROOM_DESCRIPTION) return false
      try { JSON.parse(v.settings || '{}') } catch { return false }
      return true
    case OP.MEMBER_ADD:
      return v.name.length <= LIMITS.DISPLAY_NAME && v.bio.length <= LIMITS.BIO
    case OP.POLL_VOTE:
      return v.options.length > 0 && v.options.length <= LIMITS.POLL_OPTIONS
    default:
      return true
  }
}

/* ---- ids ---- */

export function opId (op) {
  return z32.encode(op.id)
}

export function idToKey (id) {
  return typeof id === 'string' ? z32.decode(id) : id
}

export function keyToId (key) {
  return typeof key === 'string' ? key : z32.encode(key)
}

export function sameKey (a, b) {
  if (!a || !b) return false
  return b4a.equals(idToKey(a), idToKey(b))
}
